import { Injectable } from '@angular/core';
import { localeData, LongDateFormatKey } from 'moment';
import moment from 'moment-timezone';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { TimezoneDto } from '@dto';
import { RequestMethodEnum, RequestOptions } from '@models';
import { CookieService } from './cookie.service';
import { GenericService } from './generic.service';

const TIMEZONES_ENDPOINT = 'api/v1/reference/timezones';

@Injectable({ providedIn: 'root' })
export class TimezonesService {
  private _timezones: TimezoneDto[] | undefined;
  get timezones(): TimezoneDto[] | undefined {
    return this._timezones;
  }

  constructor(
    private genericService: GenericService,
    private cookieService: CookieService,
  ) {
    moment.updateLocale('en', {
      longDateFormat: {
        L: 'MM/DD/YYYY',
        l: 'M/D/YYYY',
        LL: 'MMMM Do YYYY',
        ll: 'MMM D YYYY',
        LLL: 'MMMM Do YYYY LT',
        lll: 'MMM D YYYY LT',
        LLLL: 'dddd, MMMM Do YYYY LT',
        llll: 'Do MMMM YYYY h:mm:ss a z',
        LT: 'h:mm A',
        LTS: 'h:mm:ss A',
      },
    });
  }

  timezones$(): Observable<TimezoneDto[]> {
    const options: RequestOptions<undefined, TimezoneDto[]> = {
      method: RequestMethodEnum.GET,
      path: TIMEZONES_ENDPOINT,
    };
    if (!this.cookieService.getI18NLocale()) {
      this.cookieService.setCookie('locale', 'en-us');
    }
    return this.genericService.request(options).pipe(
      tap(timezones => {
        if (timezones?.length) {
          timezones.sort((a, b) => Number(a.displayLabel) - Number(b.displayLabel));
          this._timezones = timezones;
        }
      }),
    );
  }

  getTimezoneName(timezone: string): string {
    const tz = this.timezones.find(tzone => tzone.timezoneName === timezone);
    return tz?.timezoneName;
  }

  getDateTimeDisplayLabel(
    date: string,
    format: LongDateFormatKey,
    timezone = 'America/New_York',
    keepLocalTime = false,
  ): string {
    if (!date) {
      return '';
    }
    const userLocale = localeData(this.adjustLocale(this.cookieService.getLocale()));
    let momentDate = moment(date);
    // If there is no timezone for any reason, stick with GMT Time
    if (timezone) {
      momentDate = momentDate.tz(timezone, keepLocalTime);
    }
    return momentDate.format(userLocale.longDateFormat(format));
  }

  private adjustLocale(locale: string): string {
    return locale.toLowerCase().replace('_', '-');
  }
}
